import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";

const categories = ["General", "Buyback", "Warranty", "Payments", "Support"];

const faqData = {
  General: [
    {
      question: "What is PC Care Buyback?",
      answer:
        "PC Care Buyback is a service that allows you to sell your old or unused laptops, desktops, and accessories at the best possible price through a simple and transparent process.",
    },
    {
      question: "Which devices are eligible for buyback?",
      answer:
        "We currently accept laptops, desktops, monitors, and selected accessories from major brands in working or non-working condition, subject to evaluation.",
    },
    {
      question: "How do I start a buyback request?",
      answer:
        "You can start a buyback request by filling out the Buyback Form on our website or contacting our support team. Our team will guide you through the valuation and pickup process.",
    },
  ],
  Buyback: [
    {
      question: "How is the buyback price calculated?",
      answer:
        "The buyback price is calculated based on your device’s brand, model, age, physical condition, and working status. Final pricing is confirmed after a physical inspection.",
    },
    {
      question: "Is the quoted price final?",
      answer:
        "The quoted price is indicative. The final amount is confirmed after device inspection. If the condition matches your declaration, the price remains unchanged.",
    },
    {
      question: "Do you accept non-working devices?",
      answer:
        "Yes, we accept non-working devices as well. However, their value may be lower depending on the nature and severity of the fault.",
    },
  ],
  Warranty: [
    {
      question: "Do refurbished devices come with a warranty?",
      answer:
        "Yes, all refurbished devices sold by PC Care come with a limited warranty ranging from 1 to 3 months, depending on the product category.",
    },
    {
      question: "What does the warranty cover?",
      answer:
        "The warranty covers hardware defects and technical issues arising from our refurbishment or repair work. Physical or liquid damage is not covered.",
    },
    {
      question: "How do I claim warranty service?",
      answer:
        "To claim warranty service, you must present your original invoice and report the issue within the warranty period. Our team will inspect the device before approval.",
    },
  ],
  Payments: [
    {
      question: "How will I receive my buyback payment?",
      answer:
        "Payments are made via UPI, bank transfer, or cash (for in-store transactions) within 24–48 working hours after successful device verification.",
    },
    {
      question: "Are there any hidden charges?",
      answer:
        "No, PC Care follows a transparent pricing policy. There are no hidden charges or processing fees.",
    },
    {
      question: "What happens if I reject the final price?",
      answer:
        "If you are not satisfied with the final price, you may reject the offer. In such cases, the device will be returned to you without any obligation.",
    },
  ],
  Support: [
    {
      question: "How can I contact customer support?",
      answer:
        "You can contact our support team via the Contact Us page, email us at support@pccare.com, or call us at +91 9238264606.",
    },
    {
      question: "Where is your service center located?",
      answer:
        "Our service center is located in Jabalpur, Madhya Pradesh, India. Full address details are available on our Contact page.",
    },
    {
      question: "Do you provide doorstep pickup?",
      answer:
        "Yes, we offer doorstep pickup for eligible buyback requests, subject to location availability.",
    },
  ],
};

const Faq = () => {
  const [activeCategory, setActiveCategory] = useState("General");
  const [activeIndex, setActiveIndex] = useState(null);

  const toggleFAQ = (index) => {
    setActiveIndex(index === activeIndex ? null : index);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#f8fafc] via-[#eef2ff] to-[#e0e7ff] px-4 sm:px-6 lg:px-8 py-12 sm:py-16">

      {/* TOP BAR (breadcrumb left + title center) */}
      <div className="max-w-6xl mx-auto mb-6 relative px-4">

        {/* Left breadcrumb */}
        <div className="
    flex items-center gap-3 text-sm text-gray-600
    lg:absolute lg:left-0 lg:top-1/2 lg:-translate-y-1/2
    mb-4 lg:mb-0
  ">
          <button
            onClick={() => window.history.back()}
            className="w-8 h-8 flex items-center justify-center rounded-full border border-gray-300 hover:bg-gray-100 transition"
          >
            ←
          </button>

          <span className="flex items-center gap-1 flex-wrap">
            <Link to="/" className="hover:underline">
              Home
            </Link>
            <span>/</span>
            <span className="text-gray-800 font-medium">
              FAQs
            </span>
          </span>
        </div>

        {/* Center heading */}
        <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#351F72] text-center">
          Help Center & FAQs
        </h1>

      </div>


      {/* Sub text */}
      <div className="text-center -mt-8 mb-5">
        <p className="text-sm md:text-base text-center text-gray-800 max-w-2xl mx-auto">
          Find quick answers about buyback, warranty, payments, and support.
        </p>
      </div>

      {/* Category Tabs */}
      <div className="max-w-5xl mx-auto mb-5 sm:mb-5 flex flex-wrap justify-center gap-3">
        {categories.map((cat) => (
          <button
            key={cat}
            onClick={() => {
              setActiveCategory(cat);
              setActiveIndex(null);
            }}
            className={`px-5 py-2.5 rounded-full text-sm sm:text-base font-semibold transition-all border
              ${activeCategory === cat
                ? "bg-[#351F72] text-white border-[#351F72] shadow-md"
                : "bg-white text-gray-700 border-gray-300 hover:bg-gray-100"
              }`}
          >
            {cat}
          </button>
        ))}
      </div>

      {/* FAQ List */}
      <div className="max-w-4xl mx-auto space-y-4 sm:space-y-6">
        {faqData[activeCategory].map((faq, index) => (
          <motion.div
            key={index}
            layout
            initial={{ opacity: 0, y: 25 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.4, ease: "easeOut" }}
            className="bg-white rounded-xl sm:rounded-2xl shadow-[0_10px_28px_rgba(0,0,0,0.08)] overflow-hidden border border-gray-100"
          >
            {/* Question */}
            <button
              onClick={() => toggleFAQ(index)}
              className="w-full flex items-center justify-between gap-4 px-5 sm:px-6 py-4 sm:py-5 text-left focus:outline-none"
            >
              <span className="text-base sm:text-lg font-semibold text-gray-900 leading-snug">
                {faq.question}
              </span>

              {/* Font Awesome Arrow */}
              <motion.span
                animate={{ rotate: activeIndex === index ? 180 : 0 }}
                transition={{ duration: 0.25, ease: "easeInOut" }}
                className="text-[#351F72] text-lg sm:text-xl shrink-0 flex items-center justify-center"
              >
                <i className="fas fa-chevron-down"></i>
              </motion.span>
            </button>

            {/* Answer */}
            <AnimatePresence mode="wait" initial={false}>
              {activeIndex === index && (
                <motion.div
                  layout
                  key="content"
                  initial={{ opacity: 0, scaleY: 0.95 }}
                  animate={{ opacity: 1, scaleY: 1 }}
                  exit={{ opacity: 0, scaleY: 0.95 }}
                  transition={{ duration: 0.3, ease: "easeInOut" }}
                  className="origin-top px-5 sm:px-6 pb-5 sm:pb-6 text-sm sm:text-base text-gray-700 leading-relaxed overflow-hidden"
                >
                  {faq.answer}
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        ))}
      </div>
      {/* Premium Support CTA */}
      <motion.div
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.7, delay: 0.25 }}
        className="max-w-6xl mx-auto mt-16 sm:mt-24 rounded-3xl overflow-hidden bg-gradient-to-r from-[#351F72] via-[#4a2aa5] to-[#6b46c1] shadow-[0_20px_60px_rgba(0,0,0,0.25)]"
      >
        <div className="flex flex-col md:flex-row items-center justify-between gap-6 px-6 sm:px-10 py-8 sm:py-12">

          {/* Left Content */}
          <div className="text-center md:text-left">

            {/* Heading with Icon */}
            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-2 flex items-center justify-center md:justify-start gap-2">
              <i className="fas fa-headset text-white/90"></i>
              Need More Help?
            </h2>

            <p className="text-indigo-100 text-left text-sm sm:text-base max-w-xl">
              Our expert support team is ready to assist you with buyback, warranty,
              repairs, and any service-related questions.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
            <Link
              to="/contact"
              className="inline-flex items-center justify-center gap-2 bg-white text-[#351F72] px-7 sm:px-9 py-3 rounded-xl font-semibold shadow-lg hover:bg-gray-100 transition-all text-sm sm:text-base"
            >
              <i className="fas fa-envelope"></i>
              Contact Support
            </Link>
            <a
              href="tel:+919238264606"
              className="inline-flex items-center justify-center gap-2 border border-white/40 text-white px-7 sm:px-9 py-3 rounded-xl font-semibold hover:bg-white/10 transition-all text-sm sm:text-base"
            >
              <i className="fa-solid fa-phone"></i>
              Call Now
            </a>

          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default Faq;
